import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Close from '@mui/icons-material/Close'
import Search from '@mui/icons-material/Search'
import {
  DESCRIPTION_FILTERS,
  DESCRIPTION_STATES_QUERY_KEY,
  fetchAllJobs,
  fetchDescriptionStates,
  filterJobs,
  formatCbo,
  JOB_STATUSES,
  JOBS_QUERY_KEY,
  matchesDescription,
  TooManyJobsError,
  type DescriptionFilter,
  type DescriptionState,
  type Job,
  type JobLevel,
  type JobStatus,
} from '../api/jobs'

// s69 / 2m (C3): sem filtro de nivel na tela ate o modulo de carreira -- o filtro puro continua aceitando
const SEM_NIVEL: ReadonlySet<JobLevel> = new Set()
import { BANDS_QUERY_KEY, fetchBands, functionByJob } from '../api/bands'
import { fetchMe, ME_QUERY_KEY } from '../api/session'
import { ErrorState } from '../components/ErrorState'
import { FormCodeField } from '../components/FormCodeField'
import { NewJobButton } from '../components/NewJobButton'
import { JobFunctionField } from '../components/JobFunctionField'
import { LoadingState } from '../components/LoadingState'
import { t } from '../i18n'

/**
 * Tela 4 (sessao 35) - lista de cargos, SO LEITURA. Regua visual (T11/T12):
 * tabela DENSA com cabecalho discreto, filtros em CHIPS, paginacao "1-50 de N",
 * clique na linha abre o PAINEL a direita (padrao Stripe). SEM salario (S27-6).
 * O botao do painel leva a ficha (/cargos/:id) -- a tela 5.
 *
 * s61 / E1 (decisao de Marcelo 19/10/2026): coluna "Descricao" no FIM da tabela (as colunas antigas nao mudam de
 * posicao) e filtro por estado -- Publicada vN / Rascunho / Sem descricao. Vem de uma leitura PROPRIA
 * (`/api/job-description-states`); se ela falhar (sem permissao, API antiga), a coluna mostra "—", o filtro some e a
 * lista continua -- nunca um segundo aviso de erro sobre a mesma tela.
 */

/** Texto curto do estado da descricao (celula e painel). `undefined` com leitura OK = sem descricao. */
function textoDescricao(e: DescriptionState | undefined, lido: boolean): string {
  if (!lido) return t.jobs.none
  if (!e || (e.publishedVersion == null && !e.hasDraft)) return t.jobs.descNone
  if (e.publishedVersion != null) return t.jobs.descPublished(e.publishedVersion)
  return t.jobs.descDraftOnly
}

function EstadoDescricao({ e, lido }: { e: DescriptionState | undefined; lido: boolean }) {
  const texto = textoDescricao(e, lido)
  if (lido && e?.publishedVersion == null && e?.hasDraft) {
    return <Chip size="small" variant="outlined" color="warning" label={texto} />
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
      <Typography variant="body2" color={texto === t.jobs.descNone ? 'text.secondary' : 'text.primary'} component="span">
        {texto}
      </Typography>
      {lido && e?.publishedVersion != null && e.hasDraft && (
        <Chip size="small" variant="outlined" label={t.jobs.descDraftOpen} />
      )}
    </Stack>
  )
}

export const JOBS_PER_PAGE = 50

function alternar<T>(set: ReadonlySet<T>, v: T): Set<T> {
  const n = new Set(set)
  if (n.has(v)) n.delete(v)
  else n.add(v)
  return n
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="dt">
        {rotulo}
      </Typography>
      <Typography variant="body2" component="dd" sx={{ m: 0 }}>
        {valor}
      </Typography>
    </Box>
  )
}

function Painel({ job, fechar, descricao }: { job: Job; fechar: () => void; descricao: string }) {
  return (
    <Paper
      component="aside"
      aria-label={job.name}
      variant="outlined"
      // s69 / 2q (Marcelo 28/10 19:15, "tenho que correr ate la em cima"): o painel acompanha a rolagem da lista
      sx={{
        width: { xs: '100%', md: 340 }, flexShrink: 0, p: 2, alignSelf: 'flex-start',
        position: { md: 'sticky' }, top: { md: 16 }, maxHeight: { md: 'calc(100vh - 32px)' }, overflowY: { md: 'auto' },
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h2" component="h2" sx={{ fontSize: '1.125rem' }}>
          {job.name}
        </Typography>
        <IconButton size="small" aria-label={t.jobs.panelClose} onClick={fechar}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>
      <Stack component="dl" spacing={1.5} sx={{ m: 0, mb: 2 }}>
        {/* s69 / 2m (Marcelo 28/10/2026 14:30, C1 e C3): codigo interno, nivel e carreira saem da tela ate os modulos de
            salario e carreira; continuam no banco */}
        <Campo rotulo={t.jobs.colName} valor={job.name} />
        <Campo rotulo={t.jobs.colCbo} valor={formatCbo(job.cboCode) ?? t.jobs.none} />
        <Campo rotulo={t.jobs.colStatus} valor={t.jobs.statuses[job.status]} />
        <Campo rotulo={t.jobs.colDescription} valor={descricao} />
      </Stack>
      <Button variant="outlined" size="small" component={RouterLink} to={`/cargos/${job.id}`}>
        {t.jobs.openRecord}
      </Button>
    </Paper>
  )
}

export function JobsPage() {
  const q = useQuery({ queryKey: JOBS_QUERY_KEY, queryFn: fetchAllJobs })
  const estados = useQuery({ queryKey: DESCRIPTION_STATES_QUERY_KEY, queryFn: fetchDescriptionStates, retry: false })
  const lido = estados.isSuccess
  // s62 / C3b-2 (F2): funcao de cada cargo, do catalogo da empresa; quem altera cargos escolhe direto na coluna
  const faixas = useQuery({ queryKey: BANDS_QUERY_KEY, queryFn: fetchBands, retry: false })
  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
  const podeFuncao = (me.data?.permissions ?? []).includes('job_architecture.job.write')
  const funcaoDe = useMemo(() => functionByJob(faixas.data ?? []), [faixas.data])
  const [descricoes, setDescricoes] = useState<ReadonlySet<DescriptionFilter>>(new Set())
  const [texto, setTexto] = useState('')
  const [situacoes, setSituacoes] = useState<ReadonlySet<JobStatus>>(new Set())
  const [pagina, setPagina] = useState(0)
  const [selecionado, setSelecionado] = useState<string | null>(null)

  const filtrados = useMemo(
    () =>
      filterJobs(q.data ?? [], { text: texto, levels: SEM_NIVEL, statuses: situacoes }).filter(
        (j) => !lido || matchesDescription(estados.data?.get(j.id), descricoes),
      ),
    [q.data, texto, situacoes, lido, estados.data, descricoes],
  )
  const total = filtrados.length
  const ultimaPagina = Math.max(0, Math.ceil(total / JOBS_PER_PAGE) - 1)
  const paginaAtual = Math.min(pagina, ultimaPagina)
  const visiveis = filtrados.slice(paginaAtual * JOBS_PER_PAGE, (paginaAtual + 1) * JOBS_PER_PAGE)
  const job = selecionado ? ((q.data ?? []).find((j) => j.id === selecionado) ?? null) : null
  const temFiltro = texto.trim() !== '' || situacoes.size > 0 || (lido && descricoes.size > 0)

  // Mudou o filtro: volta para a 1a pagina (senao "51-100 de 3").
  function aoFiltrar(f: () => void) {
    f()
    setPagina(0)
  }
  function limpar() {
    aoFiltrar(() => {
      setTexto('')
      setSituacoes(new Set())
      setDescricoes(new Set())
    })
  }

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1" component="h1">
          {t.jobs.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t.jobs.subtitle}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {/* s69 / 2r: criar cargo pela tela (so quem escreve cargos) */}
          {(me.data?.permissions ?? []).includes('job_architecture.job.write') && (
            <NewJobButton existentes={(q.data ?? []).map((j) => j.name)} />
          )}
          <Button size="small" variant="outlined" component={RouterLink} to="/cargos/faixas">
            {t.jobs.bandsLink}
          </Button>
          <FormCodeField pode={(me.data?.permissions ?? []).includes('tenant.update_basics')} />
        </Stack>
      </Box>

      {q.isPending && <LoadingState />}
      {q.isError &&
        (q.error instanceof TooManyJobsError ? (
          <Alert severity="warning">{t.jobs.tooMany}</Alert>
        ) : (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ))}
      {q.isSuccess && q.data.length === 0 && <Alert severity="info">{t.jobs.empty}</Alert>}
      {q.isSuccess && q.data.length > 0 && (
        <>
          <Stack spacing={1}>
            <TextField
              size="small"
              label={t.jobs.searchLabel}
              placeholder={t.jobs.searchPlaceholder}
              value={texto}
              onChange={(e) => aoFiltrar(() => setTexto(e.target.value))}
              sx={{ maxWidth: 420 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary" id="filtro-situacao">
                {t.jobs.filterStatus}
              </Typography>
              <Stack direction="row" spacing={0.5} role="group" aria-labelledby="filtro-situacao" useFlexGap flexWrap="wrap">
                {JOB_STATUSES.map((s) => (
                  <Chip
                    key={s}
                    size="small"
                    label={t.jobs.statuses[s]}
                    variant={situacoes.has(s) ? 'filled' : 'outlined'}
                    color={situacoes.has(s) ? 'primary' : 'default'}
                    aria-pressed={situacoes.has(s)}
                    onClick={() => aoFiltrar(() => setSituacoes((x) => alternar(x, s)))}
                  />
                ))}
              </Stack>
              {lido && (
                <>
                  <Typography variant="caption" color="text.secondary" id="filtro-descricao" sx={{ pl: 1 }}>
                    {t.jobs.filterDescription}
                  </Typography>
                  <Stack direction="row" spacing={0.5} role="group" aria-labelledby="filtro-descricao" useFlexGap flexWrap="wrap">
                    {DESCRIPTION_FILTERS.map((d) => (
                      <Chip
                        key={d}
                        size="small"
                        label={t.jobs.descriptionFilters[d]}
                        variant={descricoes.has(d) ? 'filled' : 'outlined'}
                        color={descricoes.has(d) ? 'primary' : 'default'}
                        aria-pressed={descricoes.has(d)}
                        onClick={() => aoFiltrar(() => setDescricoes((x) => alternar(x, d)))}
                      />
                    ))}
                  </Stack>
                </>
              )}
              {temFiltro && (
                <Button size="small" onClick={limpar}>
                  {t.jobs.clearFilters}
                </Button>
              )}
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
            <Paper variant="outlined" sx={{ flexGrow: 1, minWidth: 0 }}>
              {total === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2">{t.jobs.noMatch}</Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table size="small" aria-label={t.jobs.title}>
                      <TableHead>
                        <TableRow>
                          {[t.jobs.colName, t.jobs.colCbo, t.jobs.colStatus, t.jobs.colDescription, ...(faixas.isSuccess ? [t.jobs.colFunction] : [])].map(
                            (c) => (
                              <TableCell
                                key={c}
                                sx={{ color: 'text.secondary', fontSize: '0.75rem', fontWeight: 500, whiteSpace: 'nowrap' }}
                              >
                                {c}
                              </TableCell>
                            ),
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visiveis.map((j) => (
                          <TableRow
                            key={j.id}
                            hover
                            selected={selecionado === j.id}
                            tabIndex={0}
                            onClick={() => setSelecionado(j.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setSelecionado(j.id)
                              }
                            }}
                            sx={{ cursor: 'pointer' }}
                            data-testid={`job-${j.id}`}
                          >
                            <TableCell>{j.name}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCbo(j.cboCode) ?? t.jobs.none}</TableCell>
                            <TableCell>
                              {j.status === 'active' ? (
                                t.jobs.statuses.active
                              ) : (
                                <Chip size="small" variant="outlined" label={t.jobs.statuses[j.status]} />
                              )}
                            </TableCell>
                            <TableCell data-testid={`job-desc-${j.id}`}>
                              <EstadoDescricao e={estados.data?.get(j.id)} lido={lido} />
                            </TableCell>
                            {faixas.isSuccess && (
                              <TableCell data-testid={`job-funcao-${j.id}`}>
                                <JobFunctionField jobId={j.id} jobName={j.name} bands={faixas.data} atual={funcaoDe.get(j.id)} pode={podeFuncao} compacto />
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ px: 2, py: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" data-testid="jobs-range">
                      {t.jobs.rowsOf(
                        paginaAtual * JOBS_PER_PAGE + 1,
                        Math.min((paginaAtual + 1) * JOBS_PER_PAGE, total),
                        total,
                      )}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={t.jobs.prevPage}
                      disabled={paginaAtual === 0}
                      onClick={() => setPagina(paginaAtual - 1)}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={t.jobs.nextPage}
                      disabled={paginaAtual >= ultimaPagina}
                      onClick={() => setPagina(paginaAtual + 1)}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Stack>
                </>
              )}
            </Paper>
            {job && (
              <Painel
                job={job}
                fechar={() => setSelecionado(null)}
                descricao={textoDescricao(estados.data?.get(job.id), lido)}
              />
            )}
          </Stack>
        </>
      )}
    </Stack>
  )
}
